import type { Viewport } from '@skill-trees/shared';

export type ComputeCenteredViewportInput = {
    /** World-space point to place at the center of the available view area. */
    focusX: number;
    focusY: number;
    /** Zoom level of the resulting viewport. */
    k: number;
    availableWidth: number;
    windowHeight: number;
};

/** @returns null when `availableWidth <= 0` so callers can skip animation. */
export function computeCenteredViewport(input: ComputeCenteredViewportInput): Viewport | null {
    if (input.availableWidth <= 0) {
        return null;
    }

    const { focusX, focusY, k, availableWidth, windowHeight } = input;

    return {
        x: -focusX * k + availableWidth / 2,
        y: -focusY * k + windowHeight / 2,
        k,
    };
}

export type WorldBounds = {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
};

/**
 * World-space bounding box of all positioned nodes, expanded by half the node
 * dimensions (fx/fy are node centers). Null when no node has a position.
 */
export function computeTreeBounds(
    nodes: readonly { fx?: number; fy?: number }[],
    nodeWidth: number,
    nodeHeight: number,
): WorldBounds | null {
    let bounds: WorldBounds | null = null;
    for (const node of nodes) {
        if (node.fx === undefined || node.fy === undefined) continue;
        const minX = node.fx - nodeWidth / 2;
        const maxX = node.fx + nodeWidth / 2;
        const minY = node.fy - nodeHeight / 2;
        const maxY = node.fy + nodeHeight / 2;
        bounds = bounds
            ? {
                  minX: Math.min(bounds.minX, minX),
                  maxX: Math.max(bounds.maxX, maxX),
                  minY: Math.min(bounds.minY, minY),
                  maxY: Math.max(bounds.maxY, maxY),
              }
            : { minX, maxX, minY, maxY };
    }
    return bounds;
}

export const FIT_VIEWPORT_DEFAULT_PADDING_PX = 48;

export type ComputeFitViewportInput = {
    bounds: WorldBounds;
    availableWidth: number;
    windowHeight: number;
    /** Screen-space padding kept around the bounds. Defaults to FIT_VIEWPORT_DEFAULT_PADDING_PX. */
    paddingPx?: number;
    minK: number;
    maxK: number;
};

/**
 * Viewport that shows the whole bounds centered in the available view area,
 * zoomed out (or in) as needed within [minK, maxK].
 * @returns null when the view area or bounds are degenerate.
 */
export function computeFitViewport(input: ComputeFitViewportInput): Viewport | null {
    const { bounds, availableWidth, windowHeight, minK, maxK } = input;
    if (availableWidth <= 0 || windowHeight <= 0) return null;

    const boundsWidth = bounds.maxX - bounds.minX;
    const boundsHeight = bounds.maxY - bounds.minY;
    if (boundsWidth <= 0 || boundsHeight <= 0) return null;

    const paddingPx = input.paddingPx ?? FIT_VIEWPORT_DEFAULT_PADDING_PX;
    const usableWidth = Math.max(1, availableWidth - 2 * paddingPx);
    const usableHeight = Math.max(1, windowHeight - 2 * paddingPx);
    const fitK = Math.min(usableWidth / boundsWidth, usableHeight / boundsHeight);
    const k = Math.min(maxK, Math.max(minK, fitK));

    return computeCenteredViewport({
        focusX: (bounds.minX + bounds.maxX) / 2,
        focusY: (bounds.minY + bounds.maxY) / 2,
        k,
        availableWidth,
        windowHeight,
    });
}
